import React from "react";

/**
 * ResidencyToggle, a two-pill In-State / Out-of-State selector.
 *
 * Used directly under every college selector on the site so we capture
 * residency before any cost calculation or AI explanation runs.
 *
 * Props:
 *   value: "" | "in_state" | "out_of_state"
 *   onChange(next)
 *   disabled: when true, both pills become muted and ignore clicks
 *   disabledNote: short helper line shown beneath the pills when disabled
 *                 (e.g. "Same price for all students" for private schools)
 *   testId: prefix for the data-testid attributes
 */
export default function ResidencyToggle({
  value = "",
  onChange,
  disabled = false,
  disabledNote = "",
  testId = "residency",
  className = "",
}) {
  const choose = (v) => {
    if (disabled) return;
    onChange?.(v);
  };

  const baseBtn =
    "h-9 px-4 rounded-full text-xs font-medium transition-colors border";
  const stateActive = "border-[#1d4ed8] bg-[#eff6ff] text-[#0f172a]";
  const stateIdle = "border-[#bfdbfe] bg-white text-slate-600 hover:border-[#1d4ed8]";
  const stateDisabled = "border-[#e5e7eb] bg-[#f9fafb] text-slate-400 cursor-not-allowed";

  const cls = (active) => {
    if (disabled) return `${baseBtn} ${stateDisabled}`;
    return `${baseBtn} ${active ? stateActive : stateIdle}`;
  };

  return (
    <div className={className} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
        Residency
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => choose("in_state")}
          aria-pressed={value === "in_state"}
          disabled={disabled}
          className={cls(value === "in_state")}
          data-testid={`${testId}-in-state`}
        >
          In-State
        </button>
        <button
          type="button"
          onClick={() => choose("out_of_state")}
          aria-pressed={value === "out_of_state"}
          disabled={disabled}
          className={cls(value === "out_of_state")}
          data-testid={`${testId}-out-of-state`}
        >
          Out-of-State
        </button>
      </div>
      {disabled && disabledNote && (
        <p className="text-[11px] text-slate-500 mt-1.5" data-testid={`${testId}-note`}>
          {disabledNote}
        </p>
      )}
    </div>
  );
}

export const RESIDENCY_LABEL = {
  in_state: "In-State",
  out_of_state: "Out-of-State",
};
